import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { ImageFolder } from "../../enum/imageCategoryEnums";
import { deleteFilesInDir, generateImagesArrJson, syncImages } from "../../services/firebase.service";
import { Pagination } from "../../types/Pagination";
import { resetCacheStamp } from "../../util/cacheManager";
import { generateFilterString, generateKeywordsString, generatePaginationString, handleNetworkResponse } from "../../util/helper";
import { Part } from "../../types/Part";
import { PartsFilterType } from "../../types/filters/PartsFilterType";
import { CacheStoreKey } from "../../enum/cacheStore.enum";
import { config } from "../../config";
import { Persister } from "../../util/persister";
import { PopupManager } from "../../util/popupManager";

// Define a service using a base URL and expected endpoints
export const partsApi = createApi({
  reducerPath: "partsApi",
  tagTypes: [CacheStoreKey.userCacheStampParts],
  baseQuery: fetchBaseQuery({ baseUrl: "" }),
  endpoints: (build) => ({
    // mutation
    createPart: build.mutation({
      queryFn: async (args: { part: Part; imgs: Record<number, Blob | null> }) => {
        resetCacheStamp(CacheStoreKey.userCacheStampParts);
        // @ts-expect-error
        delete args.part.id;
        args.part.images = generateImagesArrJson(args.part.UserId, args.part.images, args.imgs, ImageFolder.Parts, config.tempResourceId);

        const response = await axios.post(
          `/part/create`,
          args.part
        );
        if (response.data.status) {
          await syncImages(args.part.UserId, ImageFolder.Parts, response.data.data.id, args.imgs);
        }
        return handleNetworkResponse(response, null);
      },
      invalidatesTags: [CacheStoreKey.userCacheStampParts],
    }),

    // mutation
    updatePart: build.mutation({
      queryFn: async (args: { part: Part; imgs?: Record<number, Blob | null> }) => {
        resetCacheStamp(CacheStoreKey.userCacheStampParts);

        if (args.imgs) {
          args.part.images = generateImagesArrJson(args.part.UserId, args.part.images, args.imgs, ImageFolder.Parts, args.part.id);
        }

        const response = await axios.post(
          `/part/update`,
          args.part
        );

        if (response.data.status && args.imgs) {
          await syncImages(args.part.UserId, ImageFolder.Parts, args.part.id, args.imgs);
        }
        return handleNetworkResponse(response, null);
      },
      invalidatesTags: [CacheStoreKey.userCacheStampParts],
    }),

    // mutation
    deletePart: build.mutation({
      queryFn: async (part: Part) => {
        resetCacheStamp(CacheStoreKey.userCacheStampParts);
        PopupManager.showInfoToast('Deleting images...');
        await deleteFilesInDir(part.UserId, ImageFolder.Parts, part.id);
        PopupManager.showInfoToast('Deleting spare part...');
        const response = await axios.post(`/part/delete/${part.id}`);
        return handleNetworkResponse(response, null);
      },
      invalidatesTags: [CacheStoreKey.userCacheStampParts],
    }),

    // read
    // this is used for load favorites
    getPartsWithIds: build.query({
      queryFn: async (args: { ids: number[] }) => {
        if (args.ids.length < 1) {
          return {
            data: null,
          };
        }

        // favorite parts are most probably belongs to other users. so use std cache here.
        const response = await Persister.requestData(CacheStoreKey.noCache, 'GET', `/part/byIds?ids=${JSON.stringify(args.ids)}`);
        return handleNetworkResponse<Part[]>(response, null);
      },
      providesTags: [CacheStoreKey.userCacheStampParts],
    }),

    // read api
    searchParts: build.query({
      queryFn: async (args: Pagination & { keywords: string, filter: Partial<PartsFilterType> }) => {
        const keywordString = generateKeywordsString(args.keywords);
        const filterString = generateFilterString(args.filter);
        const paginationString = generatePaginationString(args.limit, args.offset);

        const response = await Persister.requestData(CacheStoreKey.noCache, 'GET', `/part/search?${keywordString}&${filterString}${paginationString}`);
        return handleNetworkResponse(response, null);
      },
      providesTags: [CacheStoreKey.userCacheStampParts],
    }),

    // read api
    readUserParts: build.query({
      queryFn: async (args: { UserId: number }) => {

        const response = await Persister.requestData(CacheStoreKey.userCacheStampParts, 'GET', `/parts/user/${args.UserId}`);
        return handleNetworkResponse(response, null);
      },
      providesTags: [CacheStoreKey.userCacheStampParts],
    }),

    // read
    // Do not cache this: User might have 100s of parts, if we gonna cache all of them, it will grow the cache
    readPart: build.query({
      queryFn: async (args: { id: number, isOwner: boolean }) => {
        if (args.id === -1) {
          return {
            data: null,
          };
        }
        const response = await Persister.requestData(CacheStoreKey.noCache, 'GET', `/part/${args.id}`);
        return handleNetworkResponse(response, null);
      },
      providesTags: [CacheStoreKey.userCacheStampParts],
    }),

    // read:  No need to cache admin actions
    getNewAdditionsToParts: build.query({
      queryFn: async (type: 'model' | 'brand' | 'area') => {
        const response = await axios.get(`/part/newAdditions/${type}`);
        return handleNetworkResponse(response, []);
      },
      providesTags: [CacheStoreKey.userCacheStampParts],
    }),
  }),
});

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
export const {
  useCreatePartMutation,
  useUpdatePartMutation,
  useSearchPartsQuery,
  useLazySearchPartsQuery,
  useReadPartQuery,
  useReadUserPartsQuery,
  useLazyReadPartQuery,
  useDeletePartMutation,
  useLazyGetPartsWithIdsQuery,
  useGetNewAdditionsToPartsQuery
} = partsApi;

export type PartsApiType = typeof partsApi;